import { useEffect, useRef } from 'react'
import { ConvexClient } from 'convex/browser'
import { useAuthToken } from '@convex-dev/auth/react'
import { api } from '@aurora/convex/convex/_generated/api'
import {
  createClientSync,
  followDelta,
  patchesToSnake,
  useSyncRefresh,
  type DeltaSubscription,
} from '@aurora/platform'
import { AURORA_PLATFORM_CONFIG } from '../store/registry'
import { auroraStore } from '../store/auroraStore'

/**
 * Aurora's specifics on top of the engine's client sync binding
 * (@platform/engine binding.ts): the token storage, the Convex client and
 * query references, and the account bootstrap. The ordered boot protocol —
 * hydrate critical then deferred, bind dispatch, subscribe to deltas, refresh
 * on focus / reconnect — lives in the engine and is enforced there.
 */

const TOKEN_KEY = 'aurora:token'

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string) {
  try {
    localStorage.setItem(TOKEN_KEY, token)
  } catch {
    /* private mode — the session simply will not persist */
  }
}

export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* nothing to clear */
  }
}

/**
 * A short-lived client for the auth calls that live outside the synced store
 * (sign in, reset, account changes). Callers close it when done.
 */
export function authClient(): ConvexClient {
  return new ConvexClient(import.meta.env.VITE_CONVEX_URL ?? '')
}

const SUBSCRIPTIONS: DeltaSubscription[] = [
  // account-scoped
  { storeKey: 'children', query: api.sync.listChildren },
  { storeKey: 'members', query: api.sync.listMembers },
  // child-scoped, keyed by their store collection:
  ...([
    // the open substrate
    ['threads', 'listThreads'],
    ['messages', 'listMessages'],
    ['captures', 'listCaptures'],
    ['artifacts', 'listArtifacts'],
    ['itemMemory', 'listItemMemory'],
    ['memories', 'listMemories'],
    ['schedules', 'listSchedules'],
    // the spine
    ['skills', 'listSkills'],
    ['skillStates', 'listSkillStates'],
    ['cycles', 'listCycles'],
    ['weeks', 'listWeeks'],
    ['days', 'listDays'],
    ['blocks', 'listBlocks'],
    ['levelHistory', 'listLevelHistory'],
    ['reviews', 'listReviews'],
  ] as const)
    .map(([storeKey, queryName]) => ({
      storeKey,
      query: (api.sync as any)[queryName],
      scoped: true,
    }))
    .filter((s) => s.query),
]

// Module singleton: hydration runs once per page load and survives remounts
// (kid mode swaps the tree and ConvexSync with it).
const sync = createClientSync({
  config: AURORA_PLATFORM_CONFIG,
  store: auroraStore as any,
  applyRows: (storeKey, rows) => (auroraStore.getState() as any).syncTable(storeKey, rows),
  // Either way the wait is over: a failed read means an empty store, which
  // the views must show as empty rather than as a skeleton forever.
  onHydrated: () => (auroraStore.getState() as any).setHydrated(),
  setOnline: (online) => (auroraStore.getState() as any).setOnline(online),
})

export function ConvexSync({ convexUrl }: { convexUrl: string }) {
  const clientRef = useRef<ConvexClient | null>(null)

  const token = getToken()
  // A Convex Auth session carries a JWT instead of a legacy token. The ref
  // keeps the fetcher current across refreshes without reconnecting.
  const jwt = useAuthToken()
  const jwtRef = useRef<string | null>(null)
  jwtRef.current = jwt
  const hasJwt = jwt !== null
  const activeChildId = auroraStore((s: any) => s.ui.activeChildId)

  // ── boot: replay the cache (idempotent) ────────────────────
  useEffect(() => {
    void sync.hydrate()
  }, [])

  // ── connect: bind dispatch, open the delta subscriptions ───
  useEffect(() => {
    if (!token && !hasJwt) return
    const client = new ConvexClient(convexUrl)
    // Identity rides the websocket for Convex Auth sessions; the legacy
    // token rides the function args instead (an empty token means "resolve
    // me from the identity" server-side).
    if (!token) client.setAuth(async () => jwtRef.current)
    clientRef.current = client
    let cancelled = false

    void sync.hydrate().then(() => {
      if (cancelled) return
      // The session object epochs the binding: an account switch or HMR
      // invalidates in-flight dispatches instead of letting them land on the
      // wrong account.
      sync.connect({
        transport: {
          // Each subscription follows its watermark, so a table larger than
          // one page still hears its newest rows live (followDelta says why).
          subscribe: (query, args, onPage) =>
            followDelta((a, on) => client.onUpdate(query as any, a, on as any), args, onPage as any),
          query: (query, args) => client.query(query as any, args),
        },
        // `commandId` is set only for receipt-aware actions. The rail also
        // reads it out of the receipt envelope in `result`, so forwarding it
        // here is the explicit form of the same fact, not the only copy.
        dispatch: (actionName, args, patches, result, commandId) =>
          client.mutation(api.dispatch.dispatch, {
            token: token ?? '',
            action: actionName,
            args,
            patches: patchesToSnake(patches),
            result,
            commandId,
          }),
        baseArgs: { token: token ?? '' },
        subscriptions: SUBSCRIPTIONS,
      })
    })

    return () => {
      cancelled = true
      sync.disconnect()
      clientRef.current = null
      client.close()
    }
  }, [convexUrl, token, hasJwt])

  // ── scope: the active child gates the child-scoped queries ─
  useEffect(() => {
    sync.setScope(activeChildId ? { childId: activeChildId } : null)
  }, [activeChildId])

  // ── account, fetched once ──────────────────────────────────
  // Skill definitions are per-child rows on the spine, so there is no separate
  // framework fetch — they arrive through the same delta channel as everything.
  useEffect(() => {
    const client = clientRef.current
    if (!client || (!token && !hasJwt)) return
    let cancelled = false

    void (async () => {
      try {
        const account = await client.query(api.sync.getAccount, { token: token ?? '' })
        if (cancelled) return
        const s = auroraStore.getState() as any
        if (account) s.setAccount(account.account, account.clientState)
        if (account?.account?.active_child_id && !s.ui.activeChildId) {
          s.setActiveChild(account.account.active_child_id)
        }
      } catch {
        // A signed-up parent without a family yet: App gates on `me` and
        // shows the family step; the store simply stays empty here.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [token, hasJwt])

  // ── focus / connectivity: publish online, drain the outbox ─
  useSyncRefresh(sync)

  return null
}
